import React, { forwardRef, useRef } from 'react';
import { IconSpinner } from '../../components';
import { STEP_UPLOAD_PIC_EDIT, STEP_UPLOAD_PIC_LIST } from '../UploadImageWizard/helpers';
import css from './UploadUserAvatar.css';
import AvatarPlaceholder from './AvatarPlaceholder';
import AvatarWithImage from './AvatarWithImage';
import UploadError from './UploadError';
import classNames from 'classnames';
import { StrategySelected as Strategy } from '../../components/UploadImageWizard/modals/StrategyModal';
import { StrategiesListModal as List } from '../../components/UploadImageWizard/modals/StrategiesListModal';
import { UploadImageWizard } from '../UploadImageWizard/UploadImageWizard';
import { useUploadProfileImage } from './hooks/useUploadProfileImage';
import config from '../../config';
import ImperativeRefConsumer from './ImperativeRefConsumer';
import { convertToBase64FromBlob } from '../../util/file';
import { useIsMobile } from '../../hooks/useIsMobile';
import { IconCameraL } from '../../icons';
const { userTypeRider } = config;

const UploadUserAvatar = forwardRef(
    (
        {
            uploadImageError,
            uploadInProgress,
            currentUser,
            onImageUpload,
            onUpdateProfile,
            rootClassName,
            profileImage,
            uploadDisabled = false,
            previewAllowed = false,
        },
        refForwarded
    ) => {
        const [isMobile] = useIsMobile(1024);

        const previewRef = useRef();
        const labelRef = useRef();

        const profielImageId =
            currentUser && currentUser.profileImage ? currentUser.profileImage.id : null;
        const avatarComponentMaybe = uploadDisabled || (profielImageId && !uploadInProgress);
        const avatarOrPreviewIsSet =
            !uploadImageError &&
            !uploadInProgress &&
            avatarComponentMaybe; /**(avatarComponentMaybe || previewState.state) */

        const showAvatar = Boolean(!uploadInProgress && avatarOrPreviewIsSet);
        const showPlaceholder = Boolean(!uploadInProgress && !avatarOrPreviewIsSet);

        const currentUserType = currentUser.attributes.profile.publicData.userType;
        const isRider = currentUserType === userTypeRider;

        /** set user avatar when a new image is uploaded to the sdk */
        useUploadProfileImage({ profileImage, onUpdateProfile });

        const onFilesUpload = (files, { setUploadState, setSrc }) => {
            const goToEdit = () => setUploadState(STEP_UPLOAD_PIC_EDIT);

            setSrc(URL.createObjectURL(files[0]));

            goToEdit();
        };

        const uploadSingleBlob = (singleBlob, { setSrc, setUploadState }) =>
            convertToBase64FromBlob(singleBlob, base64data => {
                setSrc(base64data);
                setUploadState(STEP_UPLOAD_PIC_EDIT);
            });

        return (
            <>
                <div
                    className={classNames(css.root, {
                        [rootClassName]: !!rootClassName,
                    })}
                >
                    <UploadError uploadImageError={uploadImageError} />
                    {uploadInProgress && (
                        <div className={css.uploadingImageOverlay}>
                            <IconSpinner />
                        </div>
                    )}
                    <UploadImageWizard
                        uploadInProgress={uploadInProgress}
                        onImageUpload={async (file, { setStrategy, setUploadState }) => {
                            await onImageUpload({ file, id: `user_avatar_${Date.now()}` });
                            setStrategy(null);
                            setUploadState(null);
                        }}
                        renderPlaceholder={props => {
                            const { setZoom, setSrc, setUploadState } = props;
                            const goToEdit = () => setUploadState(STEP_UPLOAD_PIC_EDIT);
                            const goToList = () => setUploadState(STEP_UPLOAD_PIC_LIST);

                            const onEdit = src => {
                                if (src) {
                                    setZoom(1);
                                    setSrc(src);
                                    goToEdit();
                                } else {
                                    goToList();
                                }
                            };

                            const onUploadFromPc = () => {
                                goToList();
                                setTimeout(() => {
                                    /**
                                     * wait for a while so that the ref
                                     * has time to be attached to the dom
                                     */
                                    if (labelRef && labelRef.current) labelRef.current.click();
                                }, 500);
                            };

                            return (
                                <ImperativeRefConsumer
                                    refForwarded={refForwarded}
                                    onAvatarClick={goToList}
                                >
                                    {showAvatar && (
                                        <AvatarWithImage
                                            profielImageId={profielImageId}
                                            avatarComponentMaybe={avatarComponentMaybe}
                                            previewAllowed={previewAllowed}
                                            previewRef={previewRef}
                                            onAvatarClick={goToList}
                                            onEdit={onEdit}
                                            onUploadFromPc={onUploadFromPc}
                                            currentUser={currentUser}
                                        />
                                    )}
                                    {showPlaceholder && (
                                        <AvatarPlaceholder
                                            onAvatarClick={goToList}
                                            uploadImageError={uploadImageError}
                                        />
                                    )}
                                </ImperativeRefConsumer>
                            );
                        }}
                    >
                        <List>
                            <List.Heading />
                            <List.Container>
                                {isMobile ? (
                                    <List.PcImage
                                        onPcFileUpload={onFilesUpload}
                                        labelRef={labelRef}
                                        icon={<IconCameraL />}
                                        label="Foto machen"
                                        inputProps={{ capture: 'environment' }}
                                        key="camera-photo"
                                        fieldName="camera-photo"
                                        accept="image/*"
                                    />
                                ) : (
                                    <List.CamerPhoto />
                                )}
                                <List.PcImage
                                    onPcFileUpload={onFilesUpload}
                                    labelRef={labelRef}
                                    key="default-upload"
                                    fieldName="default-upload"
                                    accept="image/*"
                                />
                                <List.FacebookPic />
                                {isRider && <List.Default />}
                            </List.Container>
                        </List>
                        <Strategy>
                            <Strategy.CameraPhoto />
                            <Strategy.Facebook onImageUpload={uploadSingleBlob} />
                            <Strategy.Default
                                onImageUpload={async file => {
                                    const setDefaultFlag = true;
                                    await onImageUpload({
                                        file,
                                        id: `user_avatar_${Date.now()}`,
                                        isDefault: setDefaultFlag,
                                    });
                                }}
                            />
                        </Strategy>
                    </UploadImageWizard>
                </div>
            </>
        );
    }
);

export default UploadUserAvatar;
